import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Grid,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core';
import { PageTextContentType } from '../../../../../hooks/useContentMgmt';
import { useComponentSize } from '../../../../../util/style-utils';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import TextField from '../../../../text-field';
import colors from '../../../../../themes/main-colors';
import { Filter } from '../controls/Filter';
import {
  InvoiceDetails,
  InvoiceDetailsParams,
  Status,
} from '../../../../../__generated__/pge-types';
import { toDateString } from '../../../../../util/format';
import { DATE_FORMAT, InvoiceActionsType } from '../..';
import {
  TableControlsActions,
  TableControlsType,
} from '../../../../../hooks/invoice-payment-portal/useInvoiceList';
import { Sort } from '../controls/Sort';
import PictureAsPdf from '@material-ui/icons/PictureAsPdf';
import TextLink from '../../../../text-link';

type DesktopViewProps = {
  content: PageTextContentType;
  tableControls: TableControlsType;
  invoices?: InvoiceDetails[];
  invoiceActions: InvoiceActionsType;
  searchParams?: InvoiceDetailsParams;
  downloadBill: (encryptedBillId: string) => Promise<void>;
};

export const InvoiceDesktopView = (props: DesktopViewProps) => {
  const {
    content,
    searchParams,
    invoiceActions,
    tableControls,
    invoices,
    downloadBill,
  } = props;
  const theme = useTheme();
  const [filterByVal, setFilterByVal] = useState<string | undefined>('All');
  const filterByAnchorEl = useRef<HTMLInputElement>(null);
  const { width: filterByWidth } = useComponentSize(filterByAnchorEl);
  const [sortByVal, setSortByVal] = useState<string | undefined>(' ');
  const sortByAnchorEl = useRef<HTMLInputElement>(null);
  const { width: sortByWidth } = useComponentSize(sortByAnchorEl);

  useEffect(() => {
    if (content) {
      if (searchParams?.status) {
        setFilterByVal(getDisplayNameForStatus(searchParams?.status));
      }
      if (searchParams?.sort) {
        const sortByValue = Object.keys(searchParams?.sort)?.[0];
        setSortByVal(getDisplayNameForSorting(sortByValue));
      }
    }
  }, [searchParams, content]);

  const getDisplayNameForStatus = (invoiceStatus: Status) => {
    const labels = {
      [Status.All]: content.get('ALL'),
      [Status.Paid]: content.get('PAID'),
      [Status.Unpaid]: content.get('UNPAID'),
    };

    return labels[invoiceStatus];
  };

  const getDisplayNameForSorting = (sortBy: string = '') => {
    const labels = {
      dueDate: content.get('DUE_DATE'),
      billAmount: content.get('BILL_AMOUNT'),
    };

    return labels[sortBy as 'dueDate' | 'billAmount'] || '';
  };

  return (
    <>
      <Fragment>
        <Fragment>
          <Grid
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: theme.spacing(2),
            }}
          >
            <Grid css={{ width: '100%', marginLeft: theme.spacing(2) }}>
              <PopupState variant="popover" popupId="sort-by">
                {popupState => (
                  <Box>
                    <TextField
                      name={'sortBy'}
                      value={sortByVal?.trim()}
                      label={content.get('SORT_BY')}
                      ref={sortByAnchorEl}
                      {...bindTrigger(popupState)}
                      size="small"
                    />
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                      PaperProps={{
                        style: { width: sortByWidth },
                      }}
                    >
                      <Sort
                        content={content}
                        sort={val =>
                          tableControls(TableControlsActions.SORT, val)
                        }
                        setSortByVal={setSortByVal}
                        value={searchParams?.sort || undefined}
                        setOpenDrawer={val =>
                            popupState.setOpen(val as boolean)
                        }
                      />
                    </Popover>
                  </Box>
                )}
              </PopupState>
            </Grid>
            <Grid
              css={{
                width: '100%',
                marginLeft: theme.spacing(2),
                cursor: 'pointer',
              }}
            >
              <PopupState variant="popover" popupId="filter-by">
                {popupState => (
                  <Box>
                    <TextField
                      name={'filterBy'}
                      value={filterByVal}
                      label={content.get('FILTER_BY_STATUS')}
                      ref={filterByAnchorEl}
                      {...bindTrigger(popupState)}
                      size="small"
                    />
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                      PaperProps={{
                        style: { width: filterByWidth },
                      }}
                    >
                      <Box
                        css={{
                          padding: theme.spacing(1),
                        }}
                      >
                        <Filter
                          content={content}
                          filter={val =>
                            tableControls(TableControlsActions.FILTER, val)
                          }
                          setFilterByVal={setFilterByVal}
                          value={searchParams?.status!}
                          setOpenDrawer={val =>
                            popupState.setOpen(val as boolean)
                          }
                        />
                      </Box>
                    </Popover>
                  </Box>
                )}
              </PopupState>
            </Grid>
          </Grid>
        </Fragment>
        <Fragment>
          <TableContainer component={Box}>
            <Table
              size="medium"
              css={{
                borderRadius: 0,
                boxShadow: 'none',
              }}
            >
              <TableHead>
                <TableRow
                  css={{
                    borderBottom: `1px solid ${colors.disableColor}`,
                    '& th': { borderBottom: 'none' },
                  }}
                >
                  <TableCell
                    css={{
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                      width: '20%',
                    }}
                  >
                    {content.get('DUE_DATE')}
                  </TableCell>
                  <TableCell
                    css={{
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                      width: '20%',
                    }}
                    align="left"
                  >
                    {content.get('PROJECT_NUMBER')}
                  </TableCell>
                  <TableCell
                    css={{
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                      width: '30%',
                    }}
                    align="left"
                  >
                    {content.get('PROJECT_ADDRESS')}
                  </TableCell>
                  <TableCell
                    css={{
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                      width: '10%',
                    }}
                    align="right"
                  >
                    {content.get('AMOUNT_DUE')}
                  </TableCell>
                  <TableCell
                    css={{
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                      width: '10%',
                    }}
                    align="right"
                  >
                    {content.get('BILL_AMOUNT')}
                  </TableCell>
                  <TableCell
                    css={{
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                      width: '10%',
                    }}
                    align="left"
                  >
                    {content.get('REUSABLE_STATUS')}
                  </TableCell>
                  <TableCell
                    css={{
                      width: '10%',
                      fontWeight: 'bold',
                      fontSize: theme.spacing(2),
                    }}
                    align="left"
                  >
                      {content.get('ACTION')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices &&
                  invoices?.map((invoice, index) => (
                    <TableRow
                      key={index}
                      css={{
                        borderRadius: 0,
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        borderLeft: invoice.paymentAmount
                          ? 'none'
                          : `2px solid ${colors.noirBlur}`,
                      }}
                    >
                      <TableCell
                        align="left"
                        css={{
                          verticalAlign: 'top',
                          fontSize: theme.spacing(2),
                          color: colors.tableBodyData,
                        }}
                      >
                        {toDateString(invoice.dueDate, DATE_FORMAT)}
                        <PictureAsPdf
                          style={{ marginRight: 4, fontSize: 12 }}
                        />
                        <TextLink
                          style={{
                            textDecoration: 'underline',
                            marginRight: '10px',
                            fontSize: '12px',
                            display: 'inline-block',
                          }}
                          onClick={async (e: any) => {
                            e.preventDefault();
                            await downloadBill(invoice.encryptedBillId || '');
                          }}
                        >
                          {content.get('DOWNLOAD_PDF')}
                        </TextLink>
                      </TableCell>
                      <TableCell
                        align="left"
                        css={{
                          verticalAlign: 'top',
                          fontSize: theme.spacing(2),
                          color: colors.tableBodyData,
                        }}
                      >
                        {invoice.projectNum}
                      </TableCell>
                      <TableCell
                        align="left"
                        css={{
                          verticalAlign: 'top',
                          fontSize: theme.spacing(2),
                          color: colors.tableBodyData,
                        }}
                      >
                        {invoice.projectAddress}
                      </TableCell>
                      <TableCell
                        align="right"
                        css={{
                          verticalAlign: 'top',
                          fontSize: theme.spacing(2),
                          color: colors.tableBodyData,
                        }}
                      >
                        ${parseFloat(invoice?.amountDue?.toString()!).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        css={{
                          verticalAlign: 'top',
                          fontSize: theme.spacing(2),
                          color: colors.tableBodyData,
                        }}
                      >
                        ${parseFloat(invoice?.billAmount?.toString()!).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="left"
                        css={{
                          verticalAlign: 'top',
                          fontSize: theme.spacing(2),
                          color: colors.tableBodyData,
                        }}
                      >
                        {invoice?.status}
                      </TableCell>
                      <TableCell
                        align="left"
                        css={{
                          verticalAlign: 'top',
                          fontSize: theme.spacing(2),
                          color: colors.tableBodyData,
                        }}
                      >
                        {invoiceActions(invoice)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Fragment>
      </Fragment>
    </>
  );
};
